import Component from '@glimmer/component'
import t from 'ember-intl/helpers/t'
import { and, not } from 'ember-truth-helpers'
import { command, context } from 'customer-flows/utils/core'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { on } from '@ember/modifier'
import TaxesAndFeesSummary from 'customer-flows/components/booking/taxes-and-fees-summary'
import { service } from '@ember/service'
import type { IntlService } from 'ember-intl'
import type FlowRouterService from 'customer-flows/services/flow-router'
import { isPresent } from '@ember/utils'

type CartSummaryArgs = {
  Args: {
    collapsible?: boolean
    showTaxesAndFees?: boolean
    isPaymentPage?: boolean
    isMobile?: boolean
  }
  Blocks: {
    orderActions: []
  }
  Element: HTMLDivElement
}

export default class CartSummary extends Component<CartSummaryArgs> {
  @service intl: IntlService
  @service flowRouter: FlowRouterService

  @tracked expanded = false

  @context('quote/booking-quote') getActivitySnapshot: BookingQuoteStore['getActivitySnapshot']
  @context('quote/booking-quote') getLines: BookingQuoteStore['getLines']
  @context('quote/booking-quote') selectedDateTimeDuration: BookingQuoteStore['selectedDateTimeDuration']

  @context('quote') quote: QuoteStore['quote']
  @context('quote') hasTaxesAndFees: QuoteStore['hasTaxesAndFees']
  @context('quote') appliedPromoCodes: QuoteStore['appliedPromoCodes']
  @context('quote') promoCodeDiscount: QuoteStore['promoCodeDiscount']
  @context('quote') cardIssuerDiscount: QuoteStore['cardIssuerDiscount']
  @context('quote') appliedGiftCards: QuoteStore['appliedGiftCards']
  @context('quote') appliedStoreCredits: QuoteStore['appliedStoreCredits']
  @context('quote') appliedExternalVouchers: QuoteStore['appliedExternalVouchers']
  @context('quote') hasAppliedStoredValuePayments: QuoteStore['hasAppliedStoredValuePayments']
  @context('quote') orderTotal: QuoteStore['orderTotal']
  @context('quote') priceBreakdown: QuoteStore['priceBreakdown']
  @context('quote') peekProtectFee: QuoteStore['peekProtectFee']
  @context('quote') promoCodes: QuoteStore['promoCodes']
  @context('quote') isPromoCodeApplied: QuoteStore['isPromoCodeApplied']

  @context('program') orderTaxesFeesBreakdownMode: ProgramStore['orderTaxesFeesBreakdownMode']
  @context('program') pricingTransparency: ProgramStore['pricingTransparency']
  @context('program/booking-flow') hideDateTime: BookingFlowStore['hideDateTime']
  @context('program/booking-flow') overrideFreesaleText: BookingFlowStore['overrideFreesaleText']
  @context('program/booking-flow')
  shouldShowPromoCodeInCartSummary: BookingFlowStore['shouldShowPromoCodeInCartSummary']

  @command('quote') applyPromoCodeFromQueryParam: QuoteCommands['applyPromoCodeFromQueryParam']
  @context('quote:id') quoteId: QuoteStore['id']

  constructor(owner: unknown, args: any) {
    super(owner, args)
    this.applyPromoCodeFromQueryParam()
  }

  get activityName(): string | undefined {
    return this.getActivitySnapshot()?.name
  }

  get quoteLines(): QuoteLine[] {
    return this.getLines()
  }

  get slot() {
    return this.args.collapsible ? 'trigger' : 'section'
  }

  get shouldShowMobileSummary(): boolean {
    return this.args.isMobile === true && this.args.isPaymentPage !== true && this.args.collapsible === true
  }

  get shouldShowDesktopPricingTransparencyBreakdown(): boolean {
    return this.pricingTransparency === true && this.args.isMobile !== true
  }

  get shouldShowMobilePricingTransparencySummary(): boolean {
    return this.pricingTransparency === true && this.args.isMobile === true && this.args.isPaymentPage !== true
  }

  get hasAnyTaxesFeesPromosOrTips(): boolean {
    return (
      this.hasTaxesAndFees ||
      isPresent(this.promoCodeDiscount) ||
      this.cardIssuerDiscount !== undefined ||
      this.hasAppliedStoredValuePayments ||
      this.hasTip
    )
  }

  get tipsAmountFormatted(): string | undefined {
    if (parseFloat(this.priceBreakdown?.tips?.amount ?? '0')) {
      return this.priceBreakdown?.tips?.formatted
    }

    return undefined
  }

  get hasTip(): boolean {
    return isPresent(this.tipsAmountFormatted)
  }

  get shouldShowTaxesAndFeesBreakdown(): boolean {
    return this.args.showTaxesAndFees === true || this.shouldShowDesktopPricingTransparencyBreakdown
  }

  formatNegativeAmount(money: Money | undefined): string {
    if (!money?.formatted) return ''

    return `-${money.formatted}`
  }

  @action
  toggleExpanded() {
    this.expanded = !this.expanded
  }

  <template>
    <div class='w-full' data-testid='cart-summary' ...attributes>
      <il-card expanded={{this.expanded}} {{on 'toggled' this.toggleExpanded}}>
        <il-list-item data-testid='cart-summary__order-details' slot='section' header={{this.activityName}}>
          {{#unless this.hideDateTime}}
            <span data-testid='cart-summary__date-time'>{{this.selectedDateTimeDuration}}</span>
          {{/unless}}
          <div slot='right'>
            {{yield to='orderActions'}}
          </div>
        </il-list-item>

        <div
          data-testid='cart-summary__tickets'
          slot='section'
          class='custom-scrollbar flex flex-col gap-1 overflow-auto lg:max-h-[300px]'
        >
          {{#each this.quoteLines as |line|}}
            <il-list-item>
              <div class='flex gap-1'>
                <span class='w-4.5 shrink-0'>{{line.quantity}}</span>
                <span>{{line.description}}</span>
              </div>
              <span
                slot='right'
                class='text-component-bold-para-14 text-neutrals-300'
              >{{line.priceBreakdown.price.formatted}}</span>
            </il-list-item>
          {{/each}}

          {{#if this.peekProtectFee}}
            <il-list-item data-testid='cart-summary__peek-protect'>
              <span>{{t 'booking.cart-summary.peek-protect'}}</span>
              <span
                slot='right'
                class='text-component-bold-para-14 text-neutrals-300'
              >{{this.peekProtectFee.formatted}}</span>
            </il-list-item>
          {{/if}}
        </div>

        {{#if (and this.shouldShowTaxesAndFeesBreakdown this.hasAnyTaxesFeesPromosOrTips)}}
          <div slot='section' class='flex flex-col gap-1'>
            {{#if this.promoCodeDiscount}}
              {{#each this.appliedPromoCodes as |promoCode|}}
                <il-list-item data-testid='cart-summary__promo-code'>
                  <span class='text-theme-400'>
                    {{t 'booking.cart-summary.promo-code'}}{{#if this.shouldShowPromoCodeInCartSummary}}
                      -
                      {{promoCode.code}}{{/if}}
                  </span>
                  <span slot='right' class='text-component-bold-para-14 text-theme-400'>{{this.formatNegativeAmount
                      this.promoCodeDiscount
                    }}</span>
                </il-list-item>
              {{/each}}
            {{/if}}

            {{#if this.cardIssuerDiscount}}
              <il-list-item data-testid='cart-summary__card-issuer-discount'>
                <span class='text-theme-400'>{{t 'booking.cart-summary.card-issuer-discount'}}</span>
                <span slot='right' class='text-component-bold-para-14 text-theme-400'>{{this.formatNegativeAmount
                    this.cardIssuerDiscount
                  }}</span>
              </il-list-item>
            {{/if}}

            {{#if (and this.hasTaxesAndFees (not this.shouldShowMobilePricingTransparencySummary))}}
              <TaxesAndFeesSummary
                @priceBreakdown={{this.priceBreakdown}}
                @breakdownMode={{this.orderTaxesFeesBreakdownMode}}
                @pricingTransparency={{this.pricingTransparency}}
                @isPaymentPage={{@isPaymentPage}}
                @isMobile={{@isMobile}}
              />
            {{/if}}

            {{#if this.hasTip}}
              <il-list-item data-testid='taxes-and-fees-summary__tips'>
                <span>{{t 'booking.cart-summary.tips'}}</span>
                <span slot='right' class='text-component-bold-para-14 text-neutrals-300'>{{this.tipsAmountFormatted}}</span>
              </il-list-item>
            {{/if}}

            {{#if this.hasAppliedStoredValuePayments}}
              {{#each this.appliedGiftCards as |giftCard|}}
                <il-list-item data-testid='cart-summary__gift-card'>
                  <span class='text-theme-400'>{{t 'booking.cart-summary.gift-card'}}
                    - ...{{giftCard.endingRedemptionCode}}</span>
                  <span slot='right' class='text-component-bold-para-14 text-theme-400'>{{this.formatNegativeAmount
                      giftCard.authorizedAmount
                    }}</span>
                </il-list-item>
              {{/each}}

              {{#each this.appliedStoreCredits as |storeCredit|}}
                <il-list-item data-testid='cart-summary__store-credit'>
                  <span class='text-theme-400'>{{t 'booking.cart-summary.store-credit'}}
                    - ...{{storeCredit.endingRedemptionCode}}</span>
                  <span slot='right' class='text-component-bold-para-14 text-theme-400'>{{this.formatNegativeAmount
                      storeCredit.authorizedAmount
                    }}</span>
                </il-list-item>
              {{/each}}

              {{#each this.appliedExternalVouchers as |externalVoucher|}}
                <il-list-item data-testid='cart-summary__external-voucher'>
                  <span class='text-theme-400'>{{t 'booking.cart-summary.external-voucher'}}
                    - ...{{externalVoucher.endingRedemptionCode}}</span>
                  <span slot='right' class='text-component-bold-para-14 text-theme-400'>{{this.formatNegativeAmount
                      externalVoucher.authorizedAmount
                    }}</span>
                </il-list-item>
              {{/each}}
            {{/if}}
          </div>
        {{/if}}

        <il-list-item data-testid='cart-summary__order-total' slot={{this.slot}}>
          <div class='flex flex-col gap-1'>
            <span class='text-component-bold-para-16 text-neutrals-300'>{{t 'booking.cart-summary.total'}}</span>
            {{#if (and this.shouldShowMobileSummary this.hasTaxesAndFees)}}
              <TaxesAndFeesSummary
                @priceBreakdown={{this.priceBreakdown}}
                @breakdownMode={{this.orderTaxesFeesBreakdownMode}}
                @pricingTransparency={{this.pricingTransparency}}
                @isPaymentPage={{@isPaymentPage}}
                @isMobile={{@isMobile}}
              />
            {{/if}}
          </div>
          <span slot='right' class='text-component-bold-para-16 text-theme-400'>{{this.orderTotal.formatted}}</span>
        </il-list-item>
      </il-card>

    </div>
  </template>
}
